import {useEffect, useState} from "react"
import {styled} from "@mui/material/styles"
import {useParams} from "react-router-dom"
import {Typography, Dialog, DialogTitle, DialogContent, Link, Box, IconButton} from "@mui/material"
import Grid from "@mui/material/Grid"
import {paymentPlanAPI} from "@indebted/api/paymentPlanAPI"
import {useAnalytics} from "@indebted/analytics"
import {CloseIcon} from "@indebted/assets"

import {LoadingIcon} from "./Loading"
import {ErrorPage} from "./Error"

function TermsOfDiscountOffer({linkLabel}: {linkLabel?: string}) {
	const {secureCode} = useParams()
	const [Component, setComponent] = useState(<LoadingIcon />)
	const [open, setOpen] = useState(false)

	const handleClickOpen = () => {
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
	}

	useEffect(() => {
		paymentPlanAPI
			.getTermsOfDiscountOffer({secureCode: secureCode!})
			.then((res) => {
				setComponent(
					<TermsOfDiscountOfferBody
						secureCode={secureCode!}
						title={res.Title}
						sections={res.Sections}
						termsAndConditionsLabel={res.TermsAndConditionsLabel}
						termsAndConditionsURL={res.TermsAndConditionsURL}
						handleClose={handleClose}
					/>,
				)
			})
			.catch((error: any) => {
				setComponent(<ErrorPage message={error.Message} secureCode={secureCode} error={error} />)
			})
	}, [secureCode])

	return (
		<>
			<StyledLink onClick={handleClickOpen}>{linkLabel}</StyledLink>
			<Dialog open={open} onClose={handleClose} scroll="paper" aria-labelledby="offers-terms-title" maxWidth="xs">
				{Component}
			</Dialog>
		</>
	)
}

function TermsOfDiscountOfferBody({
	secureCode,
	title,
	sections,
	termsAndConditionsLabel,
	termsAndConditionsURL,
	handleClose,
}: {
	secureCode: string
	title: string
	sections: {
		Header: string
		Bulletpoints: string[]
		Paragraphs: string[]
		MissedPaymentDisclaimer: string
	}[]
	termsAndConditionsLabel: string
	termsAndConditionsURL: string
	handleClose: () => void
}) {
	const {track} = useAnalytics()

	useEffect(() => {
		track("PaymentPlanOfferTermsViewed", {
			SecureCode: secureCode,
		})
	}, [secureCode, track])

	return (
		<StyledBody>
			<DialogTitle id="offers-terms-title">
				<StyledBox>
					{title}
					<IconButton
						onClick={handleClose}
						style={{
							padding: "0px",
						}}
					>
						<CloseIcon />
					</IconButton>
				</StyledBox>
			</DialogTitle>
			<DialogContent>
				{Array.isArray(sections) &&
					sections.map((section) => {
						return (
							<div key={section.Header}>
								<Typography variant="subtitle1">{section.Header}</Typography>
								{Array.isArray(section.Bulletpoints) && (
									<>
										<ul>
											{section.Bulletpoints.map((point) => {
												return <li key={point}>{point}</li>
											})}
											<Link href={termsAndConditionsURL} target="_blank">
												{termsAndConditionsLabel}
											</Link>
										</ul>
										{section.MissedPaymentDisclaimer}
									</>
								)}
								{Array.isArray(section.Paragraphs) &&
									section.Paragraphs.map((paragraph, index) => {
										return (
											<Typography variant="body2" component="p" paragraph key={index}>
												{paragraph}
											</Typography>
										)
									})}
							</div>
						)
					})}
			</DialogContent>
		</StyledBody>
	)
}

const StyledBox = styled(Box)`
	display: flex;
	justify-content: space-between;
	align-items: end;
	svg {
		cursor: pointer;
	}
`

const StyledBody = styled(Grid)`
	overflow-x: hidden;

	div#offers-terms-title {
		padding-bottom: 0px;
	}

	h6 {
		margin-top: 5px;
		font-weight: 500;
	}

	ul {
		margin-top: 0px;
		margin-bottom: 16px;
		list-style: none;
		padding-left: 22px;
	}

	ul li:nth-of-type(odd) {
		margin-bottom: 10px;
		margin-top: 5px;
	}

	ul li:before {
		padding: 8px;
		margin-left: -30px;
		content: "✓";
	}
`

const StyledLink = styled(Link)`
	cursor: pointer;
`

export {TermsOfDiscountOffer}
